import { Button, Checkbox, Label, Spinner, TextInput } from 'flowbite-react';
import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Container from '../shared/Container';
import OfflineDriveDropdown from '../driveAdmin/offlineDriveCandidatesList/OfflineDriveDropdown';
import { useUserContext } from 'src/context/UserProvider';
import { OfflineDocumentsTypes, DriveDocument } from 'types';
import { signout } from 'src/apis/auth';
import { useMutation, useQueryClient } from 'react-query';
import Select, { SingleValue } from 'react-select';
import { useGetEligibleDrives } from 'src/hooks/offlineDrive.hooks';
import { useGetDropdownItemsByCategory } from 'src/hooks/dropdownMenu.hooks';

interface Props {
	driveUserPhone: string;
	handleSubmit: (e: FormEvent<HTMLFormElement>) => void;
	setDriveUserPhone: (input: string) => void;
	isLoading: boolean;
	selectedDrive: { label: string; value: string };
	setSelectedDrive: (drive: { label: string; value: string }) => void;
	classNamePrefix?: string;
	documents: OfflineDocumentsTypes[];
	setDocuments: (documents: OfflineDocumentsTypes[]) => void;
	selectedLocation?: { label: string; value: string } | null;
	setSelectedLocation?: (location: { label: string; value: string } | null) => void;
}

const DriveAttendanceUserPhone = ({
	driveUserPhone,
	setDriveUserPhone,
	handleSubmit,
	isLoading,
	setSelectedDrive,
	selectedDrive,
	documents,
	setDocuments,
	selectedLocation,
	setSelectedLocation,
}: Props) => {
	const { setSelectedDrive: setSelectedDriveGlobal } = useUserContext();
	const [searchParams, setSearchParams] = useSearchParams();
	const queryClient = useQueryClient();
	const driveID = searchParams.get('driveId');
	const [internalSelectedLocation, setInternalSelectedLocation] = useState<{ label: string; value: string } | null>(null);

	// Use external state if provided, otherwise use internal state
	const locationState = selectedLocation !== undefined ? selectedLocation : internalSelectedLocation;

	// Create a stable wrapper function that handles both external and internal state
	const setLocationState = useCallback((value: { label: string; value: string } | null) => {
		if (setSelectedLocation) {
			setSelectedLocation(value);
		} else {
			setInternalSelectedLocation(value);
		}
	}, [setSelectedLocation]);

	// Fetch eligible drives to get drive details - pass driveId if available
	const { data: eligibleDrivesData } = useGetEligibleDrives({ driveId: driveID || undefined });

	// Fetch placement drive labels from dropdown menu
	const { data: placementDriveData } = useGetDropdownItemsByCategory('PLACEMENT_DRIVE', undefined, true);

	// Get the selected drive details
	const selectedDriveDetails = useMemo(() => {
		if (!selectedDrive?.value) {
			return null;
		}

		// First try to find in eligibleDrives
		if (eligibleDrivesData?.eligibleDrives) {
			const drive = eligibleDrivesData.eligibleDrives.find(drive => drive._id === selectedDrive.value);
			if (drive) {
				return drive;
			}
		}
		
		// Return null - the drive needs to be in eligibleDrives for this to work
		// The issue is that getEligibleDrives filters out archived drives
		// So if the drive is archived, it won't be in the list
		return null;
	}, [selectedDrive, eligibleDrivesData]);

	// Get drive label options based on selected drive's driveLabel field
	// Only show options if the drive has drive labels selected
	const driveLabelOptions = useMemo(() => {
		if (!selectedDriveDetails) {
			return [];
		}

		// Check if drive has drive labels - if not, return empty array (don't show dropdown)
		const driveLabels: string[] = selectedDriveDetails.driveLabel
			? (Array.isArray(selectedDriveDetails.driveLabel)
				? selectedDriveDetails.driveLabel
				: [String(selectedDriveDetails.driveLabel)])
			: [];

		// If no drive labels, don't show the dropdown
		if (driveLabels.length === 0) {
			return [];
		}

		// If placement drive data is available, try to match with dropdown menu items
		if (placementDriveData?.lmsTechStacks && placementDriveData.lmsTechStacks.length > 0) {

			// Create a map of all placement drive labels for quick lookup
			const placementDriveMap = new Map<string, string>();
			placementDriveData.lmsTechStacks
				.filter(item => item.isActive)
				.forEach(item => {
					const itemLabel = item.label || item.name;
					placementDriveMap.set(itemLabel, itemLabel);
					// Also add name as a key if different from label
					if (item.name && item.name !== itemLabel) {
						placementDriveMap.set(item.name, itemLabel);
					}
				});

			// Map drive labels to options
			// Create a unique value for each label by combining label with drive ID
			const options = driveLabels
				.map((driveLabel, index) => {
					// Try to find matching placement drive item
					const matchedLabel = placementDriveMap.get(driveLabel) || driveLabel;

					// Find a drive with this specific label to get its ID
					const driveWithLabel = eligibleDrivesData?.eligibleDrives?.find(drive => {
						if (!drive.driveLabel) return false;
						const driveLabelArray: string[] = Array.isArray(drive.driveLabel)
							? drive.driveLabel
							: [String(drive.driveLabel)];
						// Check if this drive has this specific label (case-insensitive match)
						return driveLabelArray.some(label =>
							label.toLowerCase() === driveLabel.toLowerCase() ||
							label === driveLabel
						);
					});

					const driveId = String(driveWithLabel?._id || selectedDriveDetails._id);
					// Create a unique value by combining label and driveId to ensure each option is unique
					// This ensures that even if multiple labels point to the same drive, each option is distinct
					const uniqueValue = `${driveId}-${matchedLabel}`;

					return {
						label: matchedLabel,
						value: uniqueValue, // Use unique composite value
						driveId: driveId, // Store the actual drive ID separately
					};
				})
				.filter((option, index, self) => {
					// Remove duplicates based on label only (value is already unique)
					const firstIndex = self.findIndex(o => o.label === option.label);
					return index === firstIndex;
				});

			return options.length > 0 ? options : [];
		}

		// Fallback: if placement drive data is not loaded, show the labels directly
		const fallbackOptions = driveLabels.map(label => ({
			label: label,
			value: String(selectedDriveDetails._id), // Ensure value is always a string
		}));
		return fallbackOptions;
	}, [selectedDriveDetails, placementDriveData, eligibleDrivesData]);

	const signoutFn = useMutation(['signout'], signout, {
		onSuccess() {
			queryClient.invalidateQueries(['verifyCookie']);
		},
	});

	useEffect(() => {
		if (driveID) {
			const driveOption = {
				label: '',
				value: driveID,
			};
			setSelectedDrive(driveOption);
			setSelectedDriveGlobal(driveOption);
		}
	}, [driveID]);

	useEffect(() => {
		if (selectedDrive?.value) {
			searchParams.set('driveId', selectedDrive.value);
			setSearchParams(searchParams);
		}
		// Reset location when drive changes
		if (setLocationState) {
			setLocationState(null);
		}
	}, [selectedDrive, setSearchParams, setLocationState]);

	// Auto-select drive label if only one option available
	useEffect(() => {
		if (driveLabelOptions.length === 1 && !locationState?.value) {
			const autoSelectValue = {
				label: driveLabelOptions[0].label,
				value: String(driveLabelOptions[0].value) // Ensure value is a string
			};
			setLocationState(autoSelectValue);
		}
	}, [driveLabelOptions, locationState, setLocationState]);

	useEffect(() => {
		signoutFn.mutate();
	}, []);

	const handleCheckboxChange = (document: OfflineDocumentsTypes) => {
		if (documents.includes(document)) {
			setDocuments(documents.filter(doc => doc !== document));
		} else {
			setDocuments([...documents, document]);
		}
	};

	
	return (
		<Container className="flex flex-col items-center justify-center mx-auto my-auto">
			<div className="flex flex-col px-4 md:px-[24px] max-w-[560px] mx-auto rounded-[8px] shadow-[0px_2px_4px_0px_rgba(0,0,0,0.12)] bg-[#FFF] justify-center">
				<div className="text-center content-center pt-5 md:pt-[36px] pb-[10px] mr-[12px] w-[100%] mx-auto ">
					<h6 className="font-inter text-base md:text-xl font-[600] leading-[24px] text-[#1C1C28]  ">
						Digital attendance
					</h6>
				</div>
				<div className="text-center content-center w-[100%] pb-4 md:pb-[24px] max-w-[492px] mx-auto">
					<p className="font-inter text-sm md:text-[16px] font-[500] inline-block text-[#555770] leading-[24px]">
						If you have an account on LevelUp, kindly use your LevelUp details
					</p>
				</div>
				<form className={'flex flex-col pb-5 w-full gap-[10px]'} onSubmit={handleSubmit}>
					<div className="w-full">
						<div className="mb-2 block">
							<Label className={'dark:text-[#fff] text-[#111928] font-inter'} htmlFor="phone" value="Phone number " />
							<span className="text-red-600">*</span>
						</div>
						<TextInput
							id="phone"
							sizing="lg"
							type="text"
							placeholder="10 digit number"
							required={true}
							value={driveUserPhone}
							onChange={e => setDriveUserPhone(e.target.value)}
							style={{
								color: 'black',
								border: '1px solid #D1D5DB',
								backgroundColor: 'white',
							}}
						/>
					</div>
					{selectedDrive?.value && (
						<>
							{driveLabelOptions.length > 0 ? (
								<div className="w-full">
									<div className="mb-2 block">
										<Label className={'dark:text-[#fff] text-[#111928] font-inter'} htmlFor="driveLabel" value="Select Drive Label " />
										<span className="text-red-600">*</span>
									</div>
									<Select
										menuPlacement={'auto'}
										classNamePrefix="flowbite-select"
										closeMenuOnSelect={true}
										placeholder="Select drive label"
										options={driveLabelOptions}
										value={(() => {
											if (!locationState?.value || !locationState?.label) {
												return null;
											}
											// Match by BOTH label AND driveId to ensure correct option selection
											// This is critical when multiple labels share the same driveId
											const selectedOption = driveLabelOptions.find(opt => {
												const optionDriveId = (opt as any).driveId || opt.value.split('-')[0];
												const driveIdMatches = String(optionDriveId) === String(locationState.value);
												const labelMatches = String(opt.label).trim() === String(locationState.label).trim();
												// Both must match to select the correct option
												const matches = driveIdMatches && labelMatches;
												// if (matches) {
												// 	console.log('Found matching option:', { optLabel: opt.label, optDriveId: optionDriveId });
												// }
												return matches;
											});
											return selectedOption || null;
										})()}
										onChange={(value: SingleValue<{ label: string; value: string; driveId?: string }>) => {

											if (value) {
												// Extract the actual driveId from the value (format: "driveId-label")
												// The value is a composite like "698458f6b2c73629fca9b53e-Bengalore"
												const valueStr = String(value.value);
												const driveId = (value as any).driveId || valueStr.split('-')[0];
												const newValue = {
													label: String(value.label),
													value: String(driveId) // Use the actual drive ID, not the composite value
												};
												setLocationState(newValue);
											} else {
												setLocationState(null);
											}
										}}
										isClearable={false}
										isSearchable={true}
										isDisabled={false}
										blurInputOnSelect={true}
										required
									/>
									{driveLabelOptions.length === 1 && !locationState?.value && (
										<p className="text-xs text-gray-500 mt-1">
											Only one drive label available for this drive
										</p>
									)}
								</div>
							) : (
								// Debug info - remove this in production
								<div className="w-full text-xs text-gray-400 p-2 bg-gray-50 rounded mb-2">
									<div>No drive label options available</div>
									{/* <div>Debug Info:</div>
									<div>Selected Drive: {selectedDrive?.value || 'none'}</div>
									<div>Drive Found: {selectedDriveDetails ? 'yes' : 'no'}</div>
									<div>Drive Label: {selectedDriveDetails?.driveLabel ? JSON.stringify(selectedDriveDetails.driveLabel) : 'undefined'}</div>
									<div>Options Length: {driveLabelOptions.length}</div>
									<div>Placement Drive Data: {placementDriveData?.lmsTechStacks ? `${placementDriveData.lmsTechStacks.length} items` : 'not loaded'}</div> */}
								</div>
							)}
						</>
					)}
					{!driveID && (
						<div className="w-full">
							<div className="mb-2 block">
								<Label
									className={'dark:text-[#fff] text-[#111928] font-inter'}
									htmlFor="selecteddrive"
									value="Selected Drive "
								/>
								<span className="text-red-600">*</span>
							</div>
							<OfflineDriveDropdown
								isRequired={true}
								selectedDrive={selectedDrive}
								setSelectedDrive={setSelectedDrive}
								classNamePrefix="flowbite-select"
								className="max-w-[480px] w-full"
								isAutoSelect={false}
							/>
						</div>
					)}
					<div className="flex flex-col gap-[8px] py-[20px]">
						<div className="mb-2 block pr-[10px]">
							<span className="text-[#111928] font-inter text-[14px] font-[500] leading-[21px] pr-[5px]">
								Please confirm the items you’ve brought
							</span>
							<span className="text-red-600 font-inter text-[14px] font-[500] leading-[21px]">*</span>
						</div>
						<div className="flex items-center gap-4">
							{Object.values(OfflineDocumentsTypes).map(key => (
								<div key={key} className="flex items-center gap-2 cursor-pointer">
									<Checkbox
										color="purplePlaced"
										id={key}
										checked={documents.includes(key as OfflineDocumentsTypes)}
										onChange={() => handleCheckboxChange(key as OfflineDocumentsTypes)}
									/>
									<label htmlFor={key} className="cursor-pointer">
										{key.replace('_', ' ').replace(/\b\w/g, char => char.toUpperCase())}
									</label>
								</div>
							))}
						</div>
					</div>
					<Button
						color="purplePlaced"
						data-testid="auth-signup"
						disabled={
							isLoading ||
								!driveUserPhone ||
								(selectedDrive?.value && driveLabelOptions.length > 0 && (!locationState || !locationState.value))
								? true
								: false
						}
						type="submit"
						className={'signUp_form_btn'}
					>
						{isLoading ? <Spinner /> : 'Submit'}
					</Button>
				</form>
			</div>
		</Container>
	);
};

export default DriveAttendanceUserPhone;
